import { useCallback } from 'react';

import { Button } from '@change/ds-core-ui';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useNavigate } from '@change-corgi/core/react/router';
import { getWindow } from '@change-corgi/core/window';

import { useOneClickSignPetition } from 'src/app/shared/hooks/signature';
import { metaPixelTracking } from 'src/app/shared/utils/metaPixel';

import { usePublicFieldValue } from '../../../../hooks/publicFieldContext';

const GuestSignButton = ({ slug, onClick }: { slug: string; onClick?: () => void }) => {
	const { translate } = useI18n();
	const handleGuestSignClick = useCallback(() => {
		onClick?.();

		const signFormInput = document.querySelector(
			'[data-qa="sign-form"] input, [data-testid="sign-form"] input, form input[type="text"], form input[type="email"]',
		);

		if (signFormInput instanceof HTMLInputElement) {
			signFormInput.focus();
			signFormInput.scrollIntoView({ behavior: 'smooth', block: 'center' });

			getWindow().history.replaceState({}, '', `/p/${slug}?advocate=true`);
		}
	}, [slug, onClick]);

	return (
		<Button onClick={handleGuestSignClick} variant="secondary" size="small" data-qa="advocate-button">
			{translate('corgi.petition.advocates.sign_and_advocate')}
		</Button>
	);
};

const NonGuestSignButton = ({ id, slug, onClick }: { id: string; slug: string; onClick?: () => void }) => {
	const { translate } = useI18n();
	const navigate = useNavigate();
	const oneClickSignPetition = useOneClickSignPetition(id, { pageContext: 'petition_details' });
	const publicFieldValue = usePublicFieldValue();

	const handleNonGuestSignClick = useCallback(async () => {
		onClick?.();

		const data = await oneClickSignPetition({ public: publicFieldValue });

		if (data.status === 'success') {
			metaPixelTracking.trackCompleteRegistration({ source: 'community_petition_link' });
			navigate(`/p/${slug}/advocates`);
		}
	}, [oneClickSignPetition, navigate, slug, onClick, publicFieldValue]);

	return (
		<Button onClick={handleNonGuestSignClick} variant="secondary" size="small" data-qa="advocate-button">
			{translate('corgi.petition.advocates.sign_and_advocate')}
		</Button>
	);
};

export const SignButton = ({
	isGuest,
	id,
	slug,
	onClick,
}: {
	isGuest: boolean;
	id: string;
	slug: string;
	onClick?: (isGuest: boolean) => void;
}) => {
	const handleTrackClick = useCallback(() => {
		onClick?.(isGuest);
	}, [onClick, isGuest]);

	return isGuest ? (
		<GuestSignButton slug={slug} onClick={handleTrackClick} data-qa="guest-sign-advocate-button" />
	) : (
		<NonGuestSignButton id={id} slug={slug} onClick={handleTrackClick} data-qa="non-guest-sign-advocate-button" />
	);
};
